//-- copyright
// OpenProject is an open source project management software.
// Copyright (C) the OpenProject GmbH
//
// This program is free software; you can redistribute it and/or
// modify it under the terms of the GNU General Public License version 3.
//
// OpenProject is a fork of ChiliProject, which is a fork of Redmine. The copyright follows:
// Copyright (C) 2006-2013 Jean-Philippe Lang
// Copyright (C) 2010-2013 the ChiliProject Team
//
// This program is free software; you can redistribute it and/or
// modify it under the terms of the GNU General Public License
// as published by the Free Software Foundation; either version 2
// of the License, or (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program; if not, write to the Free Software
// Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA 02110-1301, USA.
//
// See COPYRIGHT and LICENSE files for more details.
//++

import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Injector, Input, OnDestroy, Output, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { States } from 'core-app/core/states/states.service';
import moment, { Moment } from 'moment';
import { StateService } from '@uirouter/core';
import { I18nService } from 'core-app/core/i18n/i18n.service';
import timeGrid from '@fullcalendar/timegrid';
import {
  CalendarOptions,
  DayCellContentArg,
  DayCellMountArg,
  DayHeaderContentArg,
  Duration,
  EventApi,
  EventInput,
  EventSourceFuncArg,
  SlotLabelContentArg,
  SlotLaneContentArg,
} from '@fullcalendar/core';
import { ConfigurationService } from 'core-app/core/config/configuration.service';
import { TimeEntryResource, formatTimeEntryEntityName } from 'core-app/features/hal/resources/time-entry-resource';
import { CollectionResource } from 'core-app/features/hal/resources/collection-resource';
import interactionPlugin from '@fullcalendar/interaction';
import { HalResourceEditingService } from 'core-app/shared/components/fields/edit/services/hal-resource-editing.service';
import { ColorsService } from 'core-app/shared/components/colors/colors.service';
import { BrowserDetector } from 'core-app/core/browser/browser-detector.service';
import { ApiV3Service } from 'core-app/core/apiv3/api-v3.service';
import { SchemaCacheService } from 'core-app/core/schemas/schema-cache.service';
import { FilterOperator } from 'core-app/shared/helpers/api-v3/api-v3-filter-builder';
import { TimezoneService } from 'core-app/core/datetime/timezone.service';
import { HalResourceNotificationService } from 'core-app/features/hal/services/hal-resource-notification.service';
import { OpCalendarService } from 'core-app/features/calendar/op-calendar.service';
import { VerboseFormattingArg } from '@fullcalendar/common';
import { firstValueFrom, Subject } from 'rxjs';
import { WeekdayService } from 'core-app/core/days/weekday.service';
import { IDay } from 'core-app/core/state/days/day.model';
import { DayResourceService } from 'core-app/core/state/days/day.service';
import allLocales from '@fullcalendar/core/locales-all';
import { TurboRequestsService } from 'core-app/core/turbo/turbo-requests.service';
import { PathHelperService } from 'core-app/core/path-helper/path-helper.service';
import { ensureId, generateId } from 'core-app/shared/helpers/dom-helpers';
import { target } from 'core-app/shared/helpers/event-helpers';
import type AnchoredPositionElement from '@openproject/primer-view-components/app/components/primer/anchored_position';
import { placePopover } from 'core-app/shared/components/anchored-popover/popover-placement';
import { liveRect } from 'core-app/shared/components/anchored-popover/live-rect';
import { visibleRect } from 'core-app/shared/components/anchored-popover/visible-rect';
import type { CaretPlacement } from 'core-app/shared/components/anchored-popover/caret-placement';
import { timeEntryPopoverHtml, timeEntryPopoverRows } from './te-calendar-popover';
import type { TimeEntrySchema } from './te-calendar-popover';
import { render } from 'lit-html';
import { DialogCloseDetail } from 'core-turbo/dialog-stream-action';

interface CalendarViewEvent {
  el:HTMLElement;
  event:EventApi;
}

interface CalendarMoveEvent {
  el:HTMLElement;
  event:EventApi;
  oldEvent:EventApi;
  delta:Duration;
  revert:() => void;
}
interface CalendarOptionsWithDayGrid extends CalendarOptions {
  dayGridClassNames:(data:DayCellMountArg) => void;
}
// An array of all the days that are displayed. The zero index represents Monday.
export type DisplayedDays = [
  boolean,
  boolean,
  boolean,
  boolean,
  boolean,
  boolean,
  boolean,
];

const TIME_ENTRY_CLASS_NAME = 'te-calendar--time-entry';
const DAY_SUM_CLASS_NAME = 'te-calendar--day-sum';
const ADD_ENTRY_CLASS_NAME = 'te-calendar--add-entry';
const ADD_ICON_CLASS_NAME = 'te-calendar--add-icon';
const ADD_ENTRY_PROHIBITED_CLASS_NAME = '-prohibited';

@Component({
  templateUrl: './te-calendar.template.html',
  styleUrls: ['./te-calendar.component.sass'],
  selector: 'op-time-entries-calendar',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    OpCalendarService,
    HalResourceEditingService,
    TurboRequestsService,
    PathHelperService,
  ],
  standalone: false,
})
export class TimeEntryCalendarComponent implements AfterViewInit, OnDestroy {
  readonly states = inject(States);
  readonly apiV3Service = inject(ApiV3Service);
  readonly $state = inject(StateService);
  private element = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly i18n = inject(I18nService);
  readonly injector = inject(Injector);
  readonly notifications = inject(HalResourceNotificationService);
  private configuration = inject(ConfigurationService);
  private timezone = inject(TimezoneService);
  private schemaCache = inject(SchemaCacheService);
  private colors = inject(ColorsService);
  private browserDetector = inject(BrowserDetector);
  private calendar = inject(OpCalendarService);
  readonly weekdayService = inject(WeekdayService);
  readonly dayService = inject(DayResourceService);
  readonly turboRequests = inject(TurboRequestsService);
  readonly pathHelper = inject(PathHelperService);

  @ViewChild(FullCalendarComponent) ucCalendar:FullCalendarComponent;

  @Input() projectIdentifier:string;

  @Input() static = false;

  @Input() set displayedDays(days:DisplayedDays) {
    this.initializeCalendar(days);
  }

  @Output() entries = new EventEmitter<CollectionResource<TimeEntryResource>>();

  // Not used by the calendar but rather is the maximum/minimum of the graph.
  public minHour = 1;

  public maxHour = 12;

  public labelIntervalHours = 2;

  public scaleRatio = 1;

  protected memoizedTimeEntries:{ start:Moment; end:Moment; entries:Promise<CollectionResource<TimeEntryResource>>; };

  public memoizedCreateAllowed = false;

  public text = {
    logTime: this.i18n.t('js.button_log_time'),
    today: this.i18n.t('js.team_planner.today'),
  };

  calendarOptions$ = new Subject<CalendarOptions>();

  public nonWorkingDays:IDay[] = [];

  private closeDialogHandler:EventListener = this.handleDialogClose.bind(this);

  private placeOpenPopover:(() => void)|null = null;

  private closeOpenPopover = () => {
    this.element.nativeElement.querySelector<HTMLElement>('.te-calendar--popover:popover-open')?.hidePopover();
  };

  private repositionOpenPopover = () => {
    this.placeOpenPopover?.();
  };

  public additionalOptions:CalendarOptionsWithDayGrid = {
    editable: false,
    locales: allLocales,
    locale: this.i18n.locale,
    fixedWeekCount: false,
    headerToolbar: {
      right: '',
      center: 'title',
      left: 'prev,next today',
    },
    buttonText: { today: this.text.today },
    initialView: 'timeGridWeek',
    firstDay: this.configuration.startOfWeek(),
    timeZone: this.configuration.isTimezoneSet() ? this.configuration.timezone() : 'local',
    hiddenDays: [],
    // This is a magic number that is derived by trial and error
    contentHeight: 550,
    slotEventOverlap: false,
    slotLabelInterval: `${this.labelIntervalHours}:00:00`,
    slotLabelFormat: (info:VerboseFormattingArg) => ((this.maxHour - info.date.hour) / this.scaleRatio).toString(),
    allDaySlot: false,
    displayEventTime: false,
    slotMinTime: `${this.minHour - 1}:00:00`,
    slotMaxTime: `${this.maxHour}:00:00`,
    events: this.calendarEventsFunction.bind(this),
    eventOverlap: (stillEvent:EventApi) => !stillEvent.classNames.includes(TIME_ENTRY_CLASS_NAME),
    plugins: [timeGrid, interactionPlugin],
    eventDidMount: this.alterEventEntry.bind(this),
    eventWillUnmount: this.beforeEventRemove.bind(this),
    eventClick: this.dispatchEventClick.bind(this),
    eventDrop: this.moveEvent.bind(this),
    dayHeaderClassNames: (data:DayHeaderContentArg) => this.calendar.applyNonWorkingDay(data, this.nonWorkingDays),
    dayCellClassNames: (data:DayCellContentArg) => this.calendar.applyNonWorkingDay(data, this.nonWorkingDays),
    dayGridClassNames: (data:DayCellContentArg) => this.calendar.applyNonWorkingDay(data, this.nonWorkingDays),
    slotLaneClassNames: (data:SlotLaneContentArg) => this.calendar.applyNonWorkingDay(data, this.nonWorkingDays),
    slotLabelClassNames: (data:SlotLabelContentArg) => this.calendar.applyNonWorkingDay(data, this.nonWorkingDays),
    dayHeaderContent: (data:DayHeaderContentArg) => this.calendar.dayHeaderContent(data),
  };

  private initializeCalendar(displayedDayss:DisplayedDays) {
    void this.weekdayService.loadWeekdays()
      .toPromise()
      .then(async () => {
        const startOfWeek = moment().startOf('week').format('YYYY-MM-DD');
        const endOfWeek = moment().endOf('week').format('YYYY-MM-DD');
        await this.requireNonWorkingDays(startOfWeek, endOfWeek);
        this.additionalOptions.hiddenDays = this.setHiddenDays(displayedDayss);
        this.calendarOptions$.next(this.additionalOptions);
      });
  }

  ngAfterViewInit():void {
    document.addEventListener('dialog:close', this.closeDialogHandler);
    window.addEventListener('resize', this.closeOpenPopover);
    document.addEventListener('scroll', this.repositionOpenPopover, { capture: true });
  }

  ngOnDestroy():void {
    document.removeEventListener('dialog:close', this.closeDialogHandler);
    window.removeEventListener('resize', this.closeOpenPopover);
    document.removeEventListener('scroll', this.repositionOpenPopover, { capture: true });
  }

  async requireNonWorkingDays(start:Date | string, end:Date | string) {
    this.nonWorkingDays = await firstValueFrom(
      this.dayService.requireNonWorkingYears$(start, end),
    );
  }

  public calendarEventsFunction(
    fetchInfo:EventSourceFuncArg,
    successCallback:(events:EventInput[]) => void,
    failureCallback:(error:Error) => void,
  ):void|PromiseLike<EventInput[]> {
    const start = moment(fetchInfo.startStr);
    const end = moment(fetchInfo.endStr);
    void this.fetchTimeEntries(start, end)
      .then(async (collection) => {
        this.entries.emit(collection);

        successCallback(await this.buildEntries(collection.elements, fetchInfo));
      })
      .catch(failureCallback);
  }

  protected fetchTimeEntries(start:Moment, end:Moment):Promise<CollectionResource<TimeEntryResource>> {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (!this.memoizedTimeEntries
      || this.memoizedTimeEntries.start.valueOf() !== start.valueOf()
      || this.memoizedTimeEntries.end.valueOf() !== end.valueOf()) {
      const promise = firstValueFrom(
        this
        .apiV3Service
        .time_entries
        .list({ filters: this.dmFilters(start, end), pageSize: 500 }),
      ).then((collection) => {
        this.memoizedCreateAllowed = !!collection.createTimeEntry;

        return collection;
      });

      this.memoizedTimeEntries = { start, end, entries: promise };
    }

    return this.memoizedTimeEntries.entries;
  }

  private async buildEntries(entries:TimeEntryResource[], fetchInfo:{ start:Date; end:Date }):Promise<EventInput[]> {
    this.setRatio(entries);
    await this.requireNonWorkingDays(fetchInfo.start, fetchInfo.end);
    return this.buildTimeEntryEntries(entries)
      .concat(this.buildAuxEntries(entries, fetchInfo));
  }

  private setRatio(entries:TimeEntryResource[]):void {
    const dateSums = this.calculateDateSums(entries);

    const maxHours = Math.max(...Object.values(dateSums), 0);

    const oldRatio = this.scaleRatio;

    if (maxHours > this.maxHour - this.minHour) {
      this.scaleRatio = this.smallerSuitableRatio((this.maxHour - this.minHour) / maxHours);
    } else {
      this.scaleRatio = 1;
    }

    if (oldRatio !== this.scaleRatio) {
      // This is a hack.
      // We already set the same function (different object) via angular.
      // But it will trigger repainting the calendar.
      // Weirdly, this.ucCalendar.getApi().rerender() does not.
      this.ucCalendar.getApi().setOption('slotLabelFormat', (info:VerboseFormattingArg) => {
          const val = (this.maxHour - info.date.hour) / this.scaleRatio;
          return val.toString();
        });
    }
  }

  private buildTimeEntryEntries(entries:TimeEntryResource[]):EventInput[] {
    const hoursDistribution:Record<string, Moment> = {};

    return entries.map((entry) => {
      let start:Moment;
      let end:Moment;
      const hours = this.timezone.toHours(entry.hours as string) * this.scaleRatio;
      const spentOn = entry.spentOn;

      if (hoursDistribution[spentOn]) {
        start = hoursDistribution[spentOn].clone().subtract(hours, 'h');
        end = hoursDistribution[spentOn].clone();
      } else {
        start = moment(spentOn).add(this.maxHour - hours, 'h');
        end = moment(spentOn).add(this.maxHour, 'h');
      }

      hoursDistribution[spentOn] = start;

      return this.timeEntry(entry, hours, start, end);
    });
  }

  private buildAuxEntries(entries:TimeEntryResource[], fetchInfo:{ start:Date; end:Date }):EventInput[] {
    const dateSums = this.calculateDateSums(entries);

    const calendarEntries:EventInput[] = [];

    for (let m = moment(this.timezone.formattedISODate(fetchInfo.start)); m.diff(fetchInfo.end, 'days') <= 0; m.add(1, 'days')) {
      const duration = dateSums[m.format('YYYY-MM-DD')] || 0;

      calendarEntries.push(this.sumEntry(m, duration));

      if (this.memoizedCreateAllowed) {
        calendarEntries.push(this.addEntry(m, duration));
      }
    }

    return calendarEntries;
  }

  private calculateDateSums(entries:TimeEntryResource[]):Record<string, number> {
    const dateSums:Record<string, number> = {};

    entries.forEach((entry) => {
      const hours = this.timezone.toHours(entry.hours as string);
      const spentOn = entry.spentOn;

      if (dateSums[spentOn]) {
        dateSums[spentOn] += hours;
      } else {
        dateSums[spentOn] = hours;
      }
    });

    return dateSums;
  }

  protected timeEntry(entry:TimeEntryResource, hours:number, start:Moment, end:Moment):EventInput {
    const color = this.colors.toHsl(this.entryName(entry));

    const classNames = [TIME_ENTRY_CLASS_NAME];

    const span = end.diff(start, 'm');

    if (span < 40) {
      classNames.push('-no-fadeout');
    }

    return {
      title: span < 20 ? '' : this.entryName(entry),
      startEditable: !!entry.update,
      start: start.format(),
      end: end.format(),
      backgroundColor: color,
      borderColor: color,
      classNames,
      entry,
    };
  }

  protected sumEntry(date:Moment, duration:number):EventInput {
    return {
      start: date.clone().add(this.maxHour - Math.min(duration * this.scaleRatio, this.maxHour - 0.5) - 0.5, 'h').format(),
      end: date.clone().add(this.maxHour - Math.min((duration + 0.05) * this.scaleRatio, this.maxHour - 0.5), 'h').format(),
      classNames: DAY_SUM_CLASS_NAME,
      rendering: 'background' as const,
      startEditable: false,
      sum: this.i18n.t('js.units.hour_string', { hours: duration.toFixed(2) }),
    };
  }

  protected addEntry(date:Moment, duration:number):EventInput {
    const classNames = [ADD_ENTRY_CLASS_NAME];

    if (duration >= 24) {
      classNames.push(ADD_ENTRY_PROHIBITED_CLASS_NAME);
    }

    return {
      start: date.clone().format(),
      end: date.clone().add(this.maxHour - Math.min(duration * this.scaleRatio, this.maxHour - 1) - 0.5, 'h').format(),
      rendering: 'background' as const,
      classNames,
    };
  }

  protected dmFilters(start:Moment, end:Moment):[string, FilterOperator, string[]][] {
    const startDate = start.format('YYYY-MM-DD');
    const endDate = end.subtract(1, 'd').format('YYYY-MM-DD');
    return [
      ['spentOn', '<>d', [startDate, endDate]] as [ string, FilterOperator, string[] ],
      ['user_id', '=', ['me']] as [string, FilterOperator, [string]],
    ];
  }

  private dispatchEventClick(event:CalendarViewEvent):void {
    if (event.event.extendedProps.entry) {
      this.editEvent(event.event.extendedProps.entry as TimeEntryResource);
    } else if (event.el.classList.contains(ADD_ENTRY_CLASS_NAME) && !event.el.classList.contains(ADD_ENTRY_PROHIBITED_CLASS_NAME)) {
      this.addEvent(moment(event.event.startStr));
    }
  }

  private editEvent(entry:TimeEntryResource):void {
    void this.turboRequests.request(
      `${this.pathHelper.timeEntryEditDialog(entry.id!)}?onlyMe=true`,
      { method: 'GET' },
    );
  }

  private moveEvent(event:CalendarMoveEvent):void {
    const entry = event.event.extendedProps.entry as TimeEntryResource;

    // Use end instead of start as when dragging, the event might be too long and would thus be start
    // on the day before by fullcalendar.
    entry.spentOn = moment(event.event.endStr).format('YYYY-MM-DD');

    void this.schemaCache.ensureLoaded(entry).then((schema) => {
      this.apiV3Service.time_entries
        .id(entry)
        .patch(entry, schema)
        .subscribe(
          (updated) => this.updateEventSet(updated, 'update'),
          (e) => {
            this.notifications.handleRawError(e);
            event.revert();
          },
        );
    });
  }

  public addEventToday():void {
    this.addEvent(moment(new Date()));
  }

  private addEvent(date:Moment):void {
    if (!this.memoizedCreateAllowed) {
      return;
    }

    void this.turboRequests.request(
      `${this.pathHelper.timeEntryDialog()}?date=${date.format('YYYY-MM-DD')}&onlyMe=true`,
      { method: 'GET' },
    );
  }

  private updateEventSet(event:TimeEntryResource, action:'update' | 'destroy' | 'create' | 'unchanged'):void {
    void this.memoizedTimeEntries.entries.then((collection) => {
      const foundIndex = collection.elements.findIndex((x) => x.id === event.id);

      switch (action) {
        case 'update':
        case 'unchanged':
          collection.elements[foundIndex] = event;
          break;
        case 'destroy':
          collection.elements.splice(foundIndex, 1);
          break;
        case 'create':
          void this.apiV3Service.time_entries.cache.updateFor(event);

          collection.elements.push(event);
          break;
        default:
          throw new Error('Invalid action');
      }

      this.ucCalendar.getApi().refetchEvents();
    });
  }

  private alterEventEntry(event:CalendarViewEvent):void {
    this.appendAddIcon(event);
    this.appendSum(event);

    if (!event.event.extendedProps.entry) {
      return;
    }

    void this.addPopover(event);
    this.prependDuration(event);
    this.appendFadeout(event);
  }

  private appendAddIcon(event:CalendarViewEvent):void {
    if (!event.el.classList.contains(ADD_ENTRY_CLASS_NAME)) {
      return;
    }

    const addIcon = document.createElement('div');
    addIcon.classList.add(ADD_ICON_CLASS_NAME);
    addIcon.innerText = '+';
    event.el.append(addIcon);
  }

  private appendSum(event:CalendarViewEvent):void {
    if (event.event.extendedProps.sum) {
      event.el.innerHTML = event.event.extendedProps.sum as string;
    }
  }

  private async addPopover(event:CalendarViewEvent):Promise<void> {
    if (this.browserDetector.isMobile) {
      return;
    }

    const entry = event.event.extendedProps.entry as TimeEntryResource;

    const schema = (await this.schemaCache.ensureLoaded(entry)) as TimeEntrySchema;

    const anchorEl = event.el;
    if (!anchorEl.isConnected) {
      return;
    }

    const anchorId = ensureId(anchorEl);
    anchorEl.role = 'button';

    const popoverId = generateId('popover');
    const rows = timeEntryPopoverRows(entry, schema, {
      placeholder: this.i18n.t('js.placeholders.default'),
      formattedDuration: this.timezone.formattedDuration(entry.hours as string),
    });
    const draw = (caret:CaretPlacement|null) => render(timeEntryPopoverHtml(popoverId, anchorId, rows, caret), anchorEl);
    draw(null);

    anchorEl.setAttribute('aria-haspopup', 'true');
    anchorEl.setAttribute('popovertarget', popoverId);

    const popoverEl = document.getElementById(popoverId) as AnchoredPositionElement;
    const anchorRect = liveRect(() => visibleRect(anchorEl));
    popoverEl.anchorElement = anchorRect as unknown as HTMLElement;
    const place = () => draw(placePopover(popoverEl, anchorRect));
    popoverEl.addEventListener('toggle', (toggle) => {
      this.placeOpenPopover = toggle.newState === 'open' ? place : null;
    });
    const showPopover = () => {
      if (popoverEl.matches(':popover-open')) return;
      popoverEl.showPopover();
      place();
    };
    const hidePopover = () => { popoverEl.hidePopover(); };

    target(anchorEl).on('mouseenter.anchor', showPopover);
    target(anchorEl).on('focus.anchor', showPopover);
    target(anchorEl).on('mouseleave.anchor', hidePopover);
    target(anchorEl).on('blur.anchor', hidePopover);
  }

  private removePopover(event:CalendarViewEvent):void {
    const anchorId = event.el.id;
    const anchorEl = document.getElementById(anchorId);
    if (!anchorEl) {
      return;
    }

    target(anchorEl).off('.anchor');
    anchorEl.removeAttribute('popovertarget');
    anchorEl.removeAttribute('aria-haspopup');
    anchorEl.removeAttribute('role');
    const popoverEl = document.querySelector(`anchored-position[anchor="${anchorId}"]`);
    if (popoverEl?.matches(':popover-open')) {
      this.placeOpenPopover = null;
    }
    popoverEl?.remove();
  }

  private prependDuration(event:CalendarViewEvent):void {
    const timeEntry = event.event.extendedProps.entry as TimeEntryResource;

    if (this.timezone.toHours(timeEntry.hours as string) < 0.5) {
      return;
    }

    const formattedDuration = this.timezone.formattedDuration(timeEntry.hours as string);

    event.el
      .querySelector('.fc-event-title')
      ?.insertAdjacentHTML('afterbegin', `<div class="fc-duration">${formattedDuration}</div>`);
  }

  /* Fade out event text to the bottom to avoid it being cut of weirdly.
   * Multiline ellipsis with an unknown height is not possible, hence we blur the text.
   * The gradient needs to take the background color of the element into account (hashed over the event
   * title) which is why the style is set in code.
   *
   * We do not print anything on short entries (< 0.5 hours),
   * which leads to the fc-short class not being applied by full calendar. For other short events, the css rules
   * need to deactivate the fc-fadeout.
   */
  private appendFadeout(event:CalendarViewEvent):void {
    const timeEntry = event.event.extendedProps.entry as TimeEntryResource;

    if (this.timezone.toHours(timeEntry.hours as string) < 0.5) {
      return;
    }

    const element = event.el;
    const fadeout = document.createElement('div');
    fadeout.classList.add('fc-fadeout');

    const hslaStart = this.colors.toHsla(this.entryName(timeEntry), 0);
    const hslaEnd = this.colors.toHsla(this.entryName(timeEntry), 100);

    fadeout.style.backgroundImage = `linear-gradient(${hslaStart} 0%, ${hslaEnd} 100%)`;

    element.append(fadeout);
  }

  private beforeEventRemove(event:CalendarViewEvent):void {
    if (!event.event.extendedProps.entry) {
      return;
    }

    this.removePopover(event);
  }

  private entryName(entry:TimeEntryResource):string {
    let { name } = entry.project;
    if (entry.entity) {
      name += ` - ${this.entityName(entry)}`;
    }

    return name ?? '-';
  }

  private entityName(entry:TimeEntryResource):string {
    return formatTimeEntryEntityName(entry.entity);
  }

  protected formatNumber(value:number):string {
    return this.i18n.toNumber(value, { precision: 2 });
  }

  private smallerSuitableRatio(value:number):number {
    for (let divisor = this.labelIntervalHours + 1; divisor < 100; divisor++) {
      const candidate = this.labelIntervalHours / divisor;

      if (value >= candidate) {
        return candidate;
      }
    }

    return 1;
  }

  protected setHiddenDays(displayedDays:DisplayedDays) {
    return Array
      .from(displayedDays, (value, index) => {
        if (!value) {
          return (index + 1) % 7;
        }
        return null;
      })
      .filter((value) => value !== null);
  }

  private handleDialogClose(event:CustomEvent<DialogCloseDetail>):void {
    const { detail: { dialog, submitted } } = event;
    if (dialog.id === 'time-entry-dialog' && submitted) {
      void this.fetchTimeEntries(
        this.memoizedTimeEntries.start,
        this.memoizedTimeEntries.end,
      ).then((collection) => {
        this.entries.emit(collection);
        this.ucCalendar.getApi().refetchEvents();
      });
    }
  }
}
